import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate } from "../lib/format";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, Send, CheckCircle2, Pencil, Trash2, Download, FileText, Repeat, PlayCircle } from "lucide-react";

const STATUS = {
  draft: "text-slate-600 bg-slate-100 border-slate-200",
  sent: "text-sky-700 bg-sky-50 border-sky-200",
  paid: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

const FREQ = { monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" };
const today = () => new Date().toISOString().slice(0, 10);
const plus14 = () => new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
const emptyForm = () => ({
  customer_name: "", customer_email: "", issue_date: today(), due_date: plus14(),
  notes: "", line_items: [{ description: "", quantity: 1, unit_price: "", treatment: "standard" }],
});
const emptyRetainer = () => ({
  customer_name: "", customer_email: "", frequency: "monthly", next_run: today(), auto_send: true,
  line_items: [{ description: "Monthly retainer", quantity: 1, unit_price: "", treatment: "standard" }],
});

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    const [inv, ret] = await Promise.all([api.get("/invoices"), api.get("/recurring-invoices")]);
    setItems(inv.data);
    setRetainers(ret.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [retainers, setRetainers] = useState([]);
  const [rOpen, setROpen] = useState(false);
  const [rEditing, setREditing] = useState(null);
  const [rForm, setRForm] = useState(emptyRetainer());
  const rSet = (k, v) => setRForm((f) => ({ ...f, [k]: v }));
  const rSetLine = (i, k, v) => setRForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const rAddLine = () => setRForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: "", treatment: "standard" }] }));
  const rRemoveLine = (i) => setRForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const rOpenNew = () => { setREditing(null); setRForm(emptyRetainer()); setROpen(true); };
  const rOpenEdit = (t) => { setREditing(t); setRForm({ customer_name: t.customer_name, customer_email: t.customer_email, frequency: t.frequency, next_run: t.next_run, auto_send: t.auto_send, notes: t.notes || "", line_items: t.line_items.map((l) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, treatment: l.treatment })) }); setROpen(true); };
  const rSave = async () => {
    if (!rForm.customer_name || !rForm.line_items.some((l) => l.description && parseFloat(l.unit_price) > 0)) { toast.error("Add a customer and a line item"); return; }
    const payload = { ...rForm, line_items: rForm.line_items.map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 1, unit_price: parseFloat(l.unit_price) || 0 })) };
    if (rEditing) await api.put(`/recurring-invoices/${rEditing.id}`, payload); else await api.post("/recurring-invoices", payload);
    toast.success(rEditing ? "Retainer updated" : "Retainer created"); setROpen(false); load();
  };
  const rRun = async (t) => { const { data } = await api.post(`/recurring-invoices/${t.id}/run`); toast.success(`Generated ${data.invoice_number} (${data.status})`); load(); };
  const rDelete = async (t) => { await api.delete(`/recurring-invoices/${t.id}`); toast.success("Retainer removed"); load(); };
  const rTotal = (t) => t.total || 0;

  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtCustomers, setStmtCustomers] = useState([]);
  const openStmt = async () => { const { data } = await api.get("/statements/customers"); setStmtCustomers(data.customers); setStmtOpen(true); };
  const sendStmt = async (c) => { try { const { data } = await api.post("/statements/send", { customer_email: c.customer_email }); toast.success(`Statement sent · ${data.count} invoice(s)`); } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: "", treatment: "standard" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const totals = form.line_items.reduce((acc, l) => {
    const net = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
    const gst = l.treatment === "standard" ? net * 0.1 : 0;
    acc.subtotal += net; acc.gst += gst;
    return acc;
  }, { subtotal: 0, gst: 0 });
  totals.total = totals.subtotal + totals.gst;

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({ customer_name: inv.customer_name, customer_email: inv.customer_email, issue_date: inv.issue_date,
      due_date: inv.due_date, notes: inv.notes || "",
      line_items: inv.line_items.map((l) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, treatment: l.treatment })) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name || !form.line_items.some((l) => l.description && parseFloat(l.unit_price) > 0)) {
      toast.error("Add a customer and at least one line item"); return;
    }
    const payload = { ...form, line_items: form.line_items.map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 1, unit_price: parseFloat(l.unit_price) || 0 })) };
    if (editing) await api.put(`/invoices/${editing.id}`, payload);
    else await api.post("/invoices", payload);
    toast.success(editing ? "Invoice updated" : "Invoice created");
    setOpen(false); load();
  };

  const send = async (inv) => {
    try { const { data } = await api.post(`/invoices/${inv.id}/send`); toast.success(`Emailed to ${inv.customer_email} · ${data.status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const markPaid = async (inv) => { await api.post(`/invoices/${inv.id}/mark-paid`); toast.success("Marked paid & income recorded"); load(); };
  const remove = async (inv) => { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); };

  const downloadPdf = (inv) => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(17, 62, 53); doc.text(`Tax Invoice ${inv.number}`, 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`To: ${inv.customer_name}`, 14, 28);
    doc.text(`Issued ${inv.issue_date}  ·  Due ${inv.due_date}`, 14, 33);
    autoTable(doc, { startY: 40, head: [["Description", "Qty", "Unit", "Amount"]],
      body: inv.line_items.map((l) => [l.description, String(l.quantity), `$${l.unit_price.toFixed(2)}`, `$${l.line_total.toFixed(2)}`]),
      theme: "grid", headStyles: { fillColor: [17, 62, 53] } });
    const y = (doc.lastAutoTable?.finalY || 100) + 10;
    doc.setFontSize(11); doc.setTextColor(20);
    doc.text(`Subtotal: ${money(inv.subtotal)}`, 140, y);
    doc.text(`GST: ${money(inv.gst)}`, 140, y + 6);
    doc.setFontSize(13); doc.text(`Total: ${money(inv.total)}`, 140, y + 14);
    doc.save(`${inv.number}.pdf`);
  };

  const outstanding = items.filter((i) => i.status !== "paid").reduce((s, i) => s + i.total, 0);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground text-sm mt-1">Create branded tax invoices, email them, and track what's unpaid.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openStmt} data-testid="statements-btn" className="gap-2"><Send className="w-4 h-4" /> Statements</Button>
          <Button onClick={openNew} data-testid="add-invoice-btn" className="gap-2"><Plus className="w-4 h-4" /> New invoice</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="inv-kpi-outstanding">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Outstanding</div>
          <div className="num text-2xl font-bold mt-2 text-rose-600">{money(outstanding)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Invoices</div>
          <div className="num text-2xl font-bold mt-2">{items.length}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Paid</div>
          <div className="num text-2xl font-bold mt-2 text-emerald-600">{items.filter((i) => i.status === "paid").length}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
        {loading ? <div className="p-10 text-center text-muted-foreground">Loading…</div> :
          items.length === 0 ? <div className="p-12 text-center text-muted-foreground">No invoices yet. Click "New invoice".</div> :
          items.map((inv) => (
            <div key={inv.id} className="flex items-center gap-4 px-5 py-4 row-hover" data-testid={`invoice-row-${inv.id}`}>
              <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center shrink-0"><FileText className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{inv.number} · {inv.customer_name}</div>
                <div className="text-xs text-muted-foreground num">Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-md border font-medium capitalize ${STATUS[inv.status]}`} data-testid={`invoice-status-${inv.id}`}>{inv.status}</span>
              <div className="num font-semibold text-sm w-24 text-right">{money(inv.total)}</div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => send(inv)} title="Email invoice" data-testid={`send-invoice-${inv.id}`} className="p-2 rounded-md hover:bg-sky-50 text-muted-foreground hover:text-sky-600 transition-colors"><Send className="w-4 h-4" /></button>
                {inv.status !== "paid" && <button onClick={() => markPaid(inv)} title="Mark paid" data-testid={`paid-invoice-${inv.id}`} className="p-2 rounded-md hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600 transition-colors"><CheckCircle2 className="w-4 h-4" /></button>}
                <button onClick={() => downloadPdf(inv)} title="PDF" className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><Download className="w-4 h-4" /></button>
                <button onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.id}`} className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(inv)} data-testid={`delete-invoice-${inv.id}`} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit ${editing.number}` : "New invoice"}</DialogTitle>
            <DialogDescription>GST is added at 10% on standard-rated lines.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1.5" data-testid="inv-customer" /></div>
              <div><Label>Customer email</Label><Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-1.5" data-testid="inv-email" placeholder="name@company.com.au" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue date</Label><Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Line items</Label>
              <div className="space-y-2 mt-1.5">
                {form.line_items.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`inv-line-${i}`}>
                    <Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className="col-span-5 h-9" placeholder="Description" data-testid={`inv-line-desc-${i}`} />
                    <Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="col-span-2 h-9" placeholder="Qty" />
                    <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="col-span-3 h-9" placeholder="Unit $" data-testid={`inv-line-price-${i}`} />
                    <Select value={l.treatment} onValueChange={(v) => setLine(i, "treatment", v)}>
                      <SelectTrigger className="col-span-1 h-9 px-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="standard">GST</SelectItem><SelectItem value="gst_free">Free</SelectItem><SelectItem value="input_taxed">Input</SelectItem></SelectContent>
                    </Select>
                    <button onClick={() => removeLine(i)} className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={addLine} data-testid="inv-add-line" className="mt-2 text-sm text-primary inline-flex items-center gap-1 hover:underline"><Plus className="w-3.5 h-3.5" /> Add line</button>
            </div>
            <div className="rounded-lg bg-secondary px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="num">{money(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="num text-sky-700">{money(totals.gst)}</span></div>
              <div className="flex justify-between font-semibold"><span>Total</span><span className="num" data-testid="inv-total">{money(totals.total)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="inv-save">{editing ? "Save" : "Create invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retainers (recurring invoices) */}
      <div className="mt-8 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Repeat className="w-4 h-4" /> Retainers</h2>
          <p className="text-muted-foreground text-sm">Auto-generate & email invoices on a schedule.</p>
        </div>
        <Button variant="outline" onClick={rOpenNew} data-testid="add-retainer-btn" className="gap-2"><Plus className="w-4 h-4" /> New retainer</Button>
      </div>
      <div className="mt-4 rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
        {retainers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No retainers yet. Set a client on a monthly schedule.</div>
        ) : retainers.map((t) => (
          <div key={t.id} className="flex items-center gap-4 px-5 py-4 row-hover" data-testid={`retainer-row-${t.id}`}>
            <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center shrink-0"><Repeat className="w-4 h-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{t.customer_name}</div>
              <div className="text-xs text-muted-foreground num">{FREQ[t.frequency]} · next {fmtDate(t.next_run)} · {t.auto_send ? "auto-sends" : "draft only"}</div>
            </div>
            <div className="num font-semibold text-sm w-24 text-right">{money(rTotal(t))}</div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => rRun(t)} title="Generate now" data-testid={`run-retainer-${t.id}`} className="p-2 rounded-md hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600 transition-colors"><PlayCircle className="w-4 h-4" /></button>
              <button onClick={() => rOpenEdit(t)} data-testid={`edit-retainer-${t.id}`} className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => rDelete(t)} data-testid={`delete-retainer-${t.id}`} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="sm:max-w-md" data-testid="statements-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Email a statement</DialogTitle>
            <DialogDescription>Customers with unpaid invoices. Send a summary of everything outstanding.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1 max-h-[50vh] overflow-y-auto">
            {stmtCustomers.length === 0 ? <div className="text-center text-muted-foreground text-sm py-8">No customers with unpaid invoices.</div> :
              stmtCustomers.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5" data-testid={`stmt-customer-${i}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.customer_name || c.customer_email}</div>
                    <div className="text-xs text-muted-foreground num">{c.count} unpaid · {money(c.total)}</div>
                  </div>
                  <Button size="sm" variant="outline" disabled={!c.customer_email} onClick={() => sendStmt(c)} data-testid={`send-stmt-${i}`} className="gap-1.5"><Send className="w-3.5 h-3.5" /> Send</Button>
                </div>
              ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setStmtOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="sm:max-w-lg" data-testid="retainer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{rEditing ? "Edit retainer" : "New retainer"}</DialogTitle>
            <DialogDescription>An invoice is created (and optionally emailed) each period.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer</Label><Input value={rForm.customer_name} onChange={(e) => rSet("customer_name", e.target.value)} className="mt-1.5" data-testid="ret-customer" /></div>
              <div><Label>Customer email</Label><Input value={rForm.customer_email} onChange={(e) => rSet("customer_email", e.target.value)} className="mt-1.5" data-testid="ret-email" placeholder="name@company.com.au" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={rForm.frequency} onValueChange={(v) => rSet("frequency", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="ret-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Next run date</Label><Input type="date" value={rForm.next_run} onChange={(e) => rSet("next_run", e.target.value)} className="mt-1.5" data-testid="ret-next-run" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div><div className="text-sm font-medium">Auto-send by email</div><div className="text-xs text-muted-foreground">Off = create as draft only</div></div>
              <Switch checked={rForm.auto_send} onCheckedChange={(v) => rSet("auto_send", v)} data-testid="ret-auto-send" />
            </div>
            <div>
              <Label>Line items</Label>
              <div className="space-y-2 mt-1.5">
                {rForm.line_items.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <Input value={l.description} onChange={(e) => rSetLine(i, "description", e.target.value)} className="col-span-5 h-9" placeholder="Description" data-testid={`ret-line-desc-${i}`} />
                    <Input type="number" value={l.quantity} onChange={(e) => rSetLine(i, "quantity", e.target.value)} className="col-span-2 h-9" placeholder="Qty" />
                    <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => rSetLine(i, "unit_price", e.target.value)} className="col-span-3 h-9" placeholder="Unit $" data-testid={`ret-line-price-${i}`} />
                    <Select value={l.treatment} onValueChange={(v) => rSetLine(i, "treatment", v)}>
                      <SelectTrigger className="col-span-1 h-9 px-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="standard">GST</SelectItem><SelectItem value="gst_free">Free</SelectItem><SelectItem value="input_taxed">Input</SelectItem></SelectContent>
                    </Select>
                    <button onClick={() => rRemoveLine(i)} className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={rAddLine} data-testid="ret-add-line" className="mt-2 text-sm text-primary inline-flex items-center gap-1 hover:underline"><Plus className="w-3.5 h-3.5" /> Add line</button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setROpen(false)}>Cancel</Button>
            <Button onClick={rSave} data-testid="ret-save">{rEditing ? "Save" : "Create retainer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
